// src/components/admin/HelpTopicsManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { helpContentService } from '../../services/helpContentService';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';
import Modal from '../common/Modal';

const emptyForm = { title: '', description: '', content: '', icon_name: 'help_outline', display_order: 0, is_active: 1 };

const HelpTopicsManagement = () => {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTopic, setEditingTopic] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [formErrors, setFormErrors] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => setToast({ show: true, message, type });

    const loadTopics = useCallback(async () => {
        try {
            setLoading(true);
            const response = await helpContentService.getHelpTopics();
            const list = response.help_topics || [];
            list.sort((a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0));
            setTopics(list);
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTopics();
    }, [loadTopics]);

    const handleAddClick = () => {
        setEditingTopic(null);
        setFormData({ ...emptyForm, display_order: topics.length + 1 });
        setFormErrors({});
        setShowModal(true);
    };

    const handleEditClick = (topic) => {
        setEditingTopic(topic);
        setFormData({
            title: topic.title || '',
            description: topic.description || '',
            content: topic.content || '',
            icon_name: topic.icon_name || 'help_outline',
            display_order: Number(topic.display_order) || 0,
            is_active: Number(topic.is_active) ? 1 : 0
        });
        setFormErrors({});
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.title.trim()) errors.title = 'Title is required';
        if (!formData.description.trim()) errors.description = 'Description is required';
        if (!formData.content.trim()) errors.content = 'Content is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSaveLoading(true);
            const payload = {
                ...formData,
                display_order: Number(formData.display_order) || 0,
                action: editingTopic ? 'update' : 'create'
            };
            if (editingTopic) {
                payload.id = editingTopic.id;
            }

            await helpContentService.updateHelpContent({ topics: [payload] });
            showToast(editingTopic ? 'Help topic updated successfully' : 'Help topic created successfully', 'success');
            setShowModal(false);
            loadTopics();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (topic) => {
        if (!window.confirm(`Are you sure you want to delete "${topic.title}"?`)) return;

        try {
            await helpContentService.updateHelpContent({ topics: [{ id: topic.id, action: 'delete' }] });
            showToast('Help topic deleted successfully', 'success');
            loadTopics();
        } catch (error) {
            showToast(error.message, 'error');
        }
    };

    return (
        <div>
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                        Help Topics
                    </h2>
                    <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>
                        Manage the help topics shown in the mobile app's Help section
                    </p>
                </div>
                <button
                    onClick={handleAddClick}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fas fa-plus"></i>
                    Add Topic
                </button>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingSpinner />
                </div>
            ) : topics.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <i className="fas fa-question-circle" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}></i>
                    <h3>No help topics found</h3>
                    <p>Add a topic to show it in the mobile app</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}>
                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Order</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Title</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Description</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '13px' }}>Icon</th>
                                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '13px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topics.map((topic) => (
                                <tr key={topic.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>{topic.display_order}</td>
                                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{topic.title}</td>
                                    <td style={{ padding: '12px', fontSize: '14px', maxWidth: '320px' }}>{topic.description}</td>
                                    <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}><code>{topic.icon_name}</code></td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                        <span style={{
                                            backgroundColor: Number(topic.is_active) ? '#28a745' : '#dc3545',
                                            color: '#fff',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}>
                                            {Number(topic.is_active) ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                        <button
                                            onClick={() => handleEditClick(topic)}
                                            style={{
                                                padding: '6px 12px',
                                                backgroundColor: '#007bff',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                marginRight: '8px'
                                            }}
                                        >
                                            <i className="fas fa-edit"></i> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(topic)}
                                            style={{
                                                padding: '6px 12px',
                                                backgroundColor: '#dc3545',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <i className="fas fa-trash"></i> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingTopic ? 'Edit Help Topic' : 'Add Help Topic'}
                size="medium"
            >
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Title <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.title ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            placeholder="e.g. Getting Started"
                        />
                        {formErrors.title && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.title}</div>}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Short Description <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.description ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            placeholder="Short summary shown in the topic list"
                        />
                        {formErrors.description && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.description}</div>}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            Content <span style={{ color: 'red' }}>*</span>
                        </label>
                        <textarea
                            name="content"
                            value={formData.content}
                            onChange={handleInputChange}
                            rows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: formErrors.content ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                            }}
                            placeholder="Full content shown when the user opens this topic"
                        />
                        {formErrors.content && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{formErrors.content}</div>}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                Icon Name
                            </label>
                            <input
                                type="text"
                                name="icon_name"
                                value={formData.icon_name}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                                placeholder="e.g. rocket_launch"
                            />
                            <div style={{ color: '#666', fontSize: '12px', marginTop: '5px' }}>
                                Material icon name used by the mobile app
                            </div>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                                Display Order
                            </label>
                            <input
                                type="number"
                                name="display_order"
                                value={formData.display_order}
                                onChange={handleInputChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active === 1}
                                onChange={handleInputChange}
                                style={{ marginRight: '8px' }}
                            />
                            <span style={{ fontWeight: '500', fontSize: '14px' }}>Active (visible in the mobile app)</span>
                        </label>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#6c757d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                            disabled={saveLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saveLoading ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                opacity: saveLoading ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            disabled={saveLoading}
                        >
                            {saveLoading && <LoadingSpinner size="small" />}
                            {saveLoading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default HelpTopicsManagement;
